import React, {useState} from 'react';
import {Layout, Dropdown, Typography, Avatar} from 'antd';
import {
    LogoutOutlined,
    UserOutlined,
    DownOutlined
} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {useMediaQuery} from 'react-responsive';
import theme from '../theme';
import packageJson from '../../package.json';
import {useUserRole, useUserSections, SECTIONAL_ROLE, PROMOTER_ROLE} from "../utils/userSessionUtils";

const {Header} = Layout;
const {Text} = Typography;

const AfHeader = ({onLogout}) => {
    const auth = useSelector((state) => state.auth || {});
    const isMobile = useMediaQuery({maxWidth: 768});
    const role = useUserRole();
    const sections = useUserSections();
    const collapsed = useSelector((state) => state.sidebar.collapsed);
    const [showAllRoutes, setShowAllRoutes] = useState(false);
    const [showAllSections, setShowAllSections] = useState(false);

    const menuItems = [
        isMobile && {
            key: "name",
            label: <b>{auth.user.fullName}</b>,
            disabled: true
        },
        {
            key: "role",
            label: (
                <>
                    <UserOutlined style={{marginRight: 8}}/>
                    Rol: {role}
                </>
            ),
            disabled: true
        },
        (role === SECTIONAL_ROLE || role === PROMOTER_ROLE) && {
            key: "section",
            label: (
                <>
                    <UserOutlined style={{marginRight: 8}}/>
                    Sección: {sections[0]}
                </>
            ),
            disabled: true
        },
        auth.user?.group && {
            key: "group",
            label: (
                <>
                    <UserOutlined style={{marginRight: 8}}/>
                    Grupo: {auth.user.group}
                </>
            ),
            disabled: true
        },
        {
            key: "version",
            label: `v${packageJson.version}`,
            disabled: true,
            style: { color: '#9ca3af', fontSize: '12px' }
        },
        {type: "divider"},
        {
            key: "logout",
            label: (
                <>
                    <LogoutOutlined style={{marginRight: 8}}/>
                    Salir
                </>
            ),
            onClick: onLogout,
            style: {color: theme.colors.danger, fontWeight: "bold", textAlign: "center"}
        }
    ].filter(Boolean);

    return (
        <>
            <Header
                style={{
                    backgroundColor: theme.colors.card,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: isMobile ? '10px 15px' : '0 20px',
                    height: isMobile ? '50px' : '64px',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                }}
            >
                <img
                    src="/promorena.png"
                    alt="Promorena"
                    style={{
                        height: isMobile ? '20px' : '28px',
                        maxWidth: isMobile ? '80px' : '120px',
                        objectFit: 'contain',
                        cursor: 'pointer',
                        transition: 'opacity 0.3s',
                    }}
                />

                <Dropdown menu={{items: menuItems}} trigger={['click']} placement="bottomRight">
                    <div style={{display: 'flex', cursor: 'pointer', gap: '10px'}}>
                        <Avatar size={40} icon={<UserOutlined/>}
                                style={{backgroundColor: theme.colors.primary, fontSize: '18px'}}/>
                        {!isMobile && (
                            <Text style={{
                                fontWeight: 'bold',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}>
                                {auth.user.fullName} <DownOutlined style={{fontSize: '12px'}}/>
                            </Text>
                        )}
                    </div>
                </Dropdown>
            </Header>
            
            <div style={{
                position: 'fixed',
                top: isMobile ? '50px' : '64px',
                left: isMobile ? 0 : (collapsed ? '80px' : '200px'),
                width: isMobile ? '100%' : `calc(100% - ${collapsed ? '80px' : '200px'})`,
                zIndex: 999,
                background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
                padding: isMobile ? '12px 16px' : '16px 20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                color: 'white'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: '1200px',
                    margin: '0'
                }}>
                    <div>
                        <div style={{
                            fontSize: isMobile ? '1.1rem' : '1.2rem',
                            fontWeight: 'bold',
                            marginBottom: '4px'
                        }}>
                            {auth.user?.username || 'Usuario'}
                        </div>
                        <div style={{
                            fontSize: isMobile ? '0.85rem' : '0.9rem',
                            opacity: 0.9,
                            display: 'flex',
                            gap: '12px',
                            flexWrap: 'wrap'
                        }}>
                            <span>Rol: {role}</span>
                            {(auth.user?.route && auth.user.route.length > 0) ? (
                                <span>
                                    • Ruta: {
                                        showAllRoutes || auth.user.route.length <= 6 
                                            ? auth.user.route.join(', ')
                                            : auth.user.route.slice(0, 6).join(', ')
                                    }
                                    {auth.user.route.length > 6 && (
                                        <span 
                                            onClick={() => setShowAllRoutes(!showAllRoutes)}
                                            style={{
                                                cursor: 'pointer',
                                                marginLeft: '8px',
                                                textDecoration: 'underline',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {showAllRoutes ? 'ver menos' : `... ver más (${auth.user.route.length - 6})`}
                                        </span>
                                    )}
                                </span>
                            ) : (
                                sections.length > 0 && (
                                    <span>
                                        • Sección: {
                                            showAllSections || sections.length <= 6 
                                                ? sections.join(', ')
                                                : sections.slice(0, 6).join(', ')
                                        }
                                        {sections.length > 6 && (
                                            <span 
                                                onClick={() => setShowAllSections(!showAllSections)}
                                                style={{
                                                    cursor: 'pointer',
                                                    marginLeft: '8px',
                                                    textDecoration: 'underline',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {showAllSections ? 'ver menos' : `... ver más (${sections.length - 6})`}
                                            </span>
                                        )}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AfHeader;
